import { GuardedDeletionService, LocationService, LocationCreateDto, LocationDto, LocationExcelDownloadDto, LocationUpdateDto } from 'orchestrator';
import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ListService } from '@abp/ng.core';
import {
  ConfirmationService,
} from '@abp/ng.theme.shared';
import { LOCATION_CONFIG, LOCATION_FIELDS } from './location.config';
import { BaseHierarchicalCrudComponent, GenericForm, ZnHierarchicalViewComponent } from '@zendra/shared';
import { FieldMapperHelper } from '@zendra/shared';

@Component({
  selector: 'app-location',
  imports: [
    CommonModule,
    GenericForm,
    ZnHierarchicalViewComponent,
  ],
  providers: [ListService, LocationService, ConfirmationService],
  templateUrl: './location.html',
  styleUrl: './location.scss',
})
export class Location extends BaseHierarchicalCrudComponent<
  LocationDto,
  LocationCreateDto,
  LocationUpdateDto,
  LocationExcelDownloadDto
> {
  private readonly guardedDeletion = inject(GuardedDeletionService);
  service = {
    ...inject(LocationService),
    deleteByIds: (ids: number[]) => this.guardedDeletion.deleteByIds('Location', ids),
  };
  config = LOCATION_CONFIG;
  fields = LOCATION_FIELDS;

  private gridHookIntervalId: any;
  private gridHooked = false;

  constructor() {
    super();

    const fkDef = this.fields.find(f => f.key === 'locationTypeId') as any;
    if (fkDef?.column) {
      fkDef.column = {
        ...fkDef.column,
        editable: false,
        editor: undefined,
        cssClass: ((fkDef.column.cssClass ? fkDef.column.cssClass + ' ' : '') + 'read-only'),
      };

      fkDef.column.cssClass = (fkDef.column.cssClass ? fkDef.column.cssClass + ' ' : '') + 'read-only';
    }
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();

    this.gridHookIntervalId = setInterval(() => {
      if (this.gridHooked) {
        clearInterval(this.gridHookIntervalId);
        return;
      }

      const grid = (this as any).crudPage?.gridTable?.grid;
      if (!grid) {
        return;
      }

      const slick = grid.slickGrid;
      if (!slick?.onBeforeEditCell) {
        clearInterval(this.gridHookIntervalId);
        return;
      }

      slick.onBeforeEditCell.subscribe((_e: any, args: any) => {
        const field = args?.column?.field ?? args?.column?.id;

        if (field === 'locationTypeId') {
          return false;
        }
        return true;
      });

      this.gridHooked = true;
      clearInterval(this.gridHookIntervalId);
    }, 250);
  }

  override ngOnDestroy(): void {
    if (this.gridHookIntervalId) {
      clearInterval(this.gridHookIntervalId);
    }
    super.ngOnDestroy();
  }

  override edit(row: LocationDto): void {
    this.selectedEntity = { ...row };

    const fields = FieldMapperHelper.mapFieldsForForm(this.fields, row, this.injector).map(f => ({
      ...f,
      required: f.required,
      validators: f.validators
    }));

    const locationTypeField = fields.find(f => (f as any).key === 'locationTypeId') as any;
    if (locationTypeField) {
      locationTypeField['disabled'] = true;
      locationTypeField['readOnly'] = true;

      if (locationTypeField['fieldOptions']) {
        locationTypeField['fieldOptions'] = {
          ...locationTypeField['fieldOptions'],
          disabled: true,
          readOnly: true,
        };
      }

      if (locationTypeField['templateOptions']) {
        locationTypeField['templateOptions'] = {
          ...locationTypeField['templateOptions'],
          disabled: true,
          readonly: true,
        };
      }

      if (locationTypeField['formControl']?.disable) {
        locationTypeField['formControl'].disable({ emitEvent: false });
      }
    }

    this.genericForm.open({
      title: `${this.localizationResource.LanguageService}::EditRecord`,
      fields,
      entityLocaleKey: this.config.localizationKey
    });
  }

  protected prepareUpdateDto(data: any, selected: LocationDto): LocationUpdateDto {
    return {
      name: data.name,
      isOrigin: data.isOrigin,
      isDestination: data.isDestination,
      latitude: data.latitude,
      longitude: data.longitude,
      area: data.area,
      isActive: data.isActive,
      concurrencyStamp: selected.concurrencyStamp,
    };
  }

  protected getEntityName(): string {
    return 'Location';
  }
}
